// src/fleet-stats/utils/date.js

export function isoToDate(iso) {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
}

export function dateToIso(date) {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export function addDays(isoDate, days) {
    const d = new Date(isoDate + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

export function daysBetween(isoA, isoB) {
    const a = new Date(isoA + "T00:00:00Z");
    const b = new Date(isoB + "T00:00:00Z");
    return Math.round((b - a) / 86400000);
}

/** Returns 0=Sun, 1=Mon ... 6=Sat for an ISO date string */
export function dayOfWeek(isoDate) {
    return new Date(isoDate + "T00:00:00Z").getUTCDay();
}

export function isWeekend(isoDate) {
    const d = dayOfWeek(isoDate);
    return d === 0 || d === 6;
}

/** Build a list of ISO date strings covering [from, to] inclusive */
export function dateRange(fromIso, toIso) {
    const dates = [];
    let cur = fromIso;
    while (cur <= toIso) {
        dates.push(cur);
        cur = addDays(cur, 1);
    }
    return dates;
}

/**
 * Detect whether a robot (or site) operates on weekends based on its own data.
 *
 * Logic:
 *   - Count weekend days in the window where uptime_s > 0
 *   - Count total weekend days in the window
 *   - If weekend_active_rate < 20%, the robot does not operate weekends
 *   - Requires at least 4 weekend day observations to make a reliable call.
 *     If fewer, returns null (insufficient data — caller should fall back to
 *     site-level pattern or include all days).
 *
 * @param {Array} rows - daily rows for a single robot within a time window
 * @returns {boolean|null} true = operates weekends, false = doesn't, null = unknown
 */
export function detectOperatesWeekends(rows) {
    const weekendRows = rows.filter((r) => isWeekend(r.date));

    // Need at least 4 weekend observations to make a reliable call
    if (weekendRows.length < 4) return null;

    const activeWeekendRows = weekendRows.filter(
        (r) => (r.uptime_s || 0) > 0 || r.moving_time_s > 0
    );

    const rate = activeWeekendRows.length / weekendRows.length;
    // < 20% weekend activity → doesn't operate weekends
    return rate >= 0.2;
}

/**
 * Smart weekend filter for a set of rows.
 *
 * Uses detectOperatesWeekends to decide whether to strip weekends.
 * Falls back to siteOperatesWeekends if robot has insufficient weekend data.
 * If both are null (truly unknown), includes all days — safer than false drops.
 *
 * Always falls back to all days if filtering would leave fewer than minSamples rows.
 *
 * @param {Array} rows - daily rows to filter
 * @param {boolean|null} robotOperatesWeekends - from detectOperatesWeekends
 * @param {boolean|null} siteOperatesWeekends  - site-level fallback
 * @param {number} minSamples - minimum rows to keep (default 3)
 * @returns {Array} filtered rows
 */
export function smartWeekdayFilter(rows, robotOperatesWeekends, siteOperatesWeekends, minSamples = 3) {
    // Resolve operating pattern: robot → site fallback → assume operates (include all)
    const operatesWeekends = robotOperatesWeekends ?? siteOperatesWeekends ?? true;

    if (operatesWeekends) {
        // Site runs weekends — use all days
        return rows;
    }

    // Site doesn't run weekends — strip them
    const weekdayRows = rows.filter((r) => !isWeekend(r.date));

    // Safety: if stripping weekends leaves too few samples, fall back to all days
    return weekdayRows.length >= minSamples ? weekdayRows : rows;
}

/**
 * Compute site-level weekend operating pattern.
 * Takes all rows for a site and returns whether the majority of robots
 * at that site operate on weekends.
 *
 * Used as fallback for new robots with insufficient weekend history.
 *
 * @param {Array} siteRows - all daily rows for a site
 * @returns {boolean|null}
 */
export function detectSiteOperatesWeekends(siteRows) {
    // Group by robot
    const byRobot = new Map();
    for (const r of siteRows) {
        if (!byRobot.has(r.robotId)) byRobot.set(r.robotId, []);
        byRobot.get(r.robotId).push(r);
    }

    const patterns = [];
    for (const robotRows of byRobot.values()) {
        const pattern = detectOperatesWeekends(robotRows);
        if (pattern !== null) patterns.push(pattern);
    }

    if (patterns.length === 0) return null;

    // Majority vote across robots at the site
    const operatesCount = patterns.filter(Boolean).length;
    return operatesCount / patterns.length >= 0.5;
}